import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BookOpen, Check, ChevronLeft, ChevronRight } from "lucide-react";

export default function PlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback((day) =>
    api.get(`/plans/${planId}`, { params: day ? { day } : {} })
      .then((r) => setPlan(r.data))
      .catch(() => navigate("/plans")), [planId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!plan) return null;

  const unit = plan.self_paced ? "Step" : "Day";
  const pct = plan.days_total ? Math.round((plan.completed_count / plan.days_total) * 100) : 0;
  const dayIdx = plan.day.index;

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/plans/${planId}/start`);
      toast.success("Your journey begins. One portion at a time.");
      await load(dayIdx);
    } finally { setBusy(false); }
  };

  const toggleDay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plans/${planId}/day/${dayIdx}/complete`);
      if (data.completed) {
        toast.success(`${unit} ${dayIdx} complete. Well done.`);
        await load(Math.min(dayIdx + 1, plan.days_total));
      } else {
        toast.success("Marked as not done.");
        await load(dayIdx);
      }
    } catch { toast.error("Could not update."); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <button data-testid="plan-back-button" onClick={() => navigate("/plans")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12">
        <ArrowLeft className="w-4 h-4" /> Reading Plans
      </button>

      <div className="animate-fade-up">
        <p className="label-overline mb-2">{plan.tagline}</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none" data-testid="plan-title">{plan.name}</h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">{plan.description}</p>

        <div className="bg-card border border-border rounded-md p-6 mt-8" data-testid="plan-progress-card">
          <div className="flex items-center justify-between mb-3">
            <p className="label-overline">Your progress</p>
            <p className="font-heading text-2xl">{plan.completed_count} <span className="text-muted-foreground text-base">/ {plan.days_total} · {pct}%</span></p>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {!plan.started && (
          <button data-testid="plan-start-button" onClick={start} disabled={busy}
            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-7 py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60 mt-8">
            Begin this plan <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="mt-12 animate-fade-up">
        <div className="flex items-center justify-between mb-6">
          <button data-testid="plan-prev-day" disabled={dayIdx <= 1} onClick={() => load(dayIdx - 1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">
            <ChevronLeft className="w-4 h-4" /> {unit} {dayIdx - 1}
          </button>
          <div className="text-center">
            <p className="font-heading text-2xl leading-none" data-testid="plan-day-label">{unit} {dayIdx} <span className="text-muted-foreground text-base">of {plan.days_total}</span></p>
            {dayIdx !== plan.current_day && (
              <button data-testid="plan-jump-current" onClick={() => load(plan.current_day)}
                className="text-xs text-primary underline underline-offset-2 hover:text-accent transition-colors mt-1">
                Jump to {unit.toLowerCase()} {plan.current_day}
              </button>
            )}
          </div>
          <button data-testid="plan-next-day" disabled={dayIdx >= plan.days_total} onClick={() => load(dayIdx + 1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">
            {unit} {dayIdx + 1} <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {plan.day.readings.map((r) => (
            <Link key={`${r.slug}-${r.chapter}`} to={`/scripture/${r.slug}/read?chapter=${r.chapter}`}
              data-testid={`plan-reading-${r.slug}-${r.chapter}`}
              className="group flex items-center justify-between bg-card border border-border rounded-md px-6 py-4 hover:bg-muted/40 transition-colors duration-300">
              <div className="flex items-center gap-3">
                <BookOpen className="w-4 h-4 text-accent" />
                <p className="font-heading text-xl">{r.book} {r.chapter}</p>
              </div>
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground group-hover:text-primary transition-colors">
                Read <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Link>
          ))}
        </div>

        <button data-testid="plan-complete-day-button" onClick={toggleDay} disabled={busy}
          className={`flex items-center gap-2 rounded-sm px-7 py-3.5 transition-colors disabled:opacity-60 mt-8 ${
            plan.day.completed ? "bg-muted text-foreground border border-border hover:bg-muted/70" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
          <Check className="w-4 h-4" /> {plan.day.completed ? `${unit} completed — tap to undo` : `Mark ${unit.toLowerCase()} complete`}
        </button>
      </div>
    </div>
  );
}
